import React, { useEffect, useState, useMemo } from 'react';
import { getAvailableDates } from '../api';

const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const WEEKDAY_FULL = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
const MONTH_NAMES = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

// Helpers for "year-month" comparison as a single integer.
const ymOf = (y, m) => y * 12 + m;
const ymFromDateStr = (iso) => {
    const [y, m] = iso.split('-').map(Number);
    return ymOf(y, m - 1);
};

export default function StepDate({ service, onSelect, onBack }) {
    const [dates, setDates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMonth, setViewMonth] = useState(() => {
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    });

    useEffect(() => {
        getAvailableDates(service.id)
            .then(setDates)
            .catch(() => setDates([]))
            .finally(() => setLoading(false));
    }, [service.id]);

    // Min/max year-month with at least one bookable date. Empty months in between
    // are still walkable — only the outer edges block navigation.
    const monthBounds = useMemo(() => {
        if (service.service_type === 'WEEKLY') return null;
        const ym = dates.filter(d => d.is_available).map(d => ymFromDateStr(d.date));
        if (!ym.length) return null;
        return { min: Math.min(...ym), max: Math.max(...ym) };
    }, [dates, service.service_type]);

    // Keep the calendar inside the bookable window. The inner bounds check makes
    // this a no-op while the user navigates within range, so it only "snaps" on
    // initial load (May → first bookable month) or after dates reload.
    useEffect(() => {
        if (!monthBounds) return;
        const current = ymOf(viewMonth.year, viewMonth.month);
        if (current < monthBounds.min || current > monthBounds.max) {
            const y = Math.floor(monthBounds.min / 12);
            const m = monthBounds.min % 12;
            setViewMonth({ year: y, month: m });
        }
    }, [monthBounds, viewMonth.year, viewMonth.month]);

    // ── WEEKLY: group by weekday ────────────────────────────────────────────
    const weekdayGroups = useMemo(() => {
        if (service.service_type !== 'WEEKLY') return null;
        // Find the nearest date per weekday and show its free capacity
        const nearest = {};
        dates.forEach(d => {
            if (!nearest[d.weekday] || d.date < nearest[d.weekday].date) {
                nearest[d.weekday] = d;
            }
        });
        return Object.values(nearest)
            .map(d => ({
                weekday: d.weekday,
                freeCapacity: d.free_capacity,
                totalCapacity: d.total_capacity,
                nearestDate: d.date,
                isAvailable: d.is_available,
            }))
            .sort((a, b) => a.weekday - b.weekday);
    }, [dates, service.service_type]);

    // ── Calendar for N_DAYS_FORWARD / DATE_RANGES ───────────────────────────
    const dateMap = useMemo(() => {
        const m = {};
        dates.forEach(d => { m[d.date] = d; });
        return m;
    }, [dates]);

    const calendarDays = useMemo(() => {
        if (service.service_type === 'WEEKLY') return [];
        const { year, month } = viewMonth;
        const firstDay = new Date(year, month, 1);
        let startWeekday = firstDay.getDay() - 1;
        if (startWeekday < 0) startWeekday = 6;
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const grid = [];
        for (let i = 0; i < startWeekday; i++) {
            grid.push({ day: null });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const info = dateMap[iso];
            grid.push({
                day: d,
                date: iso,
                available: info ? info.is_available : false,
                slots: info ? info.available_slots : 0,
                hasData: !!info,
            });
        }
        return grid;
    }, [viewMonth, dateMap, service.service_type]);

    const currentYm = ymOf(viewMonth.year, viewMonth.month);
    const canPrev = !!monthBounds && currentYm > monthBounds.min;
    const canNext = !!monthBounds && currentYm < monthBounds.max;

    const prevMonth = () => {
        if (!canPrev) return;
        setViewMonth(prev => {
            const m = prev.month - 1;
            return m < 0 ? { year: prev.year - 1, month: 11 } : { ...prev, month: m };
        });
    };

    const nextMonth = () => {
        if (!canNext) return;
        setViewMonth(prev => {
            const m = prev.month + 1;
            return m > 11 ? { year: prev.year + 1, month: 0 } : { ...prev, month: m };
        });
    };

    if (loading) {
        return <div className="loading"><div className="spinner" /></div>;
    }

    // ═══ WEEKLY: weekday cards ═══════════════════════════════════════════════
    if (service.service_type === 'WEEKLY' && weekdayGroups) {
        return (
            <div>
                <button className="btn-back" onClick={onBack}>&larr; {service.name}</button>
                <div className="section-title">Выберите день недели</div>
                <div className="weekday-grid">
                    {weekdayGroups.map(g => (
                        <div
                            key={g.weekday}
                            className={`weekday-card${!g.isAvailable ? ' disabled' : ''}`}
                            onClick={() => g.isAvailable && onSelect(g.nearestDate)}
                        >
                            <div className="weekday-card-name">{WEEKDAY_FULL[g.weekday]}</div>
                            <div className="weekday-card-slots">
                                {g.freeCapacity > 0
                                    ? `${g.freeCapacity} свободных мест`
                                    : 'Нет мест'}
                            </div>
                        </div>
                    ))}
                </div>
                {weekdayGroups.length === 0 && (
                    <div className="loading"><p>Нет доступных дней</p></div>
                )}
            </div>
        );
    }

    // ═══ N_DAYS_FORWARD / DATE_RANGES: calendar ═════════════════════════════
    if (!monthBounds) {
        return (
            <div>
                <button className="btn-back" onClick={onBack}>&larr; {service.name}</button>
                <div className="section-title">Выберите дату</div>
                <div className="loading"><p>Нет доступных дат для записи</p></div>
            </div>
        );
    }

    return (
        <div>
            <button className="btn-back" onClick={onBack}>&larr; {service.name}</button>
            <div className="section-title">Выберите дату</div>

            <div className="calendar">
                <div className="calendar-header">
                    <button className="calendar-nav" onClick={prevMonth} disabled={!canPrev}>&laquo;</button>
                    <h3>{MONTH_NAMES[viewMonth.month]} {viewMonth.year}</h3>
                    <button className="calendar-nav" onClick={nextMonth} disabled={!canNext}>&raquo;</button>
                </div>

                <div className="calendar-weekdays">
                    {WEEKDAY_SHORT.map(w => <span key={w}>{w}</span>)}
                </div>

                <div className="calendar-days">
                    {calendarDays.map((cell, i) => {
                        if (!cell.day) {
                            return <div key={i} className="calendar-day empty" />;
                        }
                        const isAvailable = cell.available;
                        return (
                            <div
                                key={i}
                                className={`calendar-day${!isAvailable ? ' disabled' : ''}${!cell.hasData ? ' disabled' : ''}`}
                                onClick={() => isAvailable && cell.hasData && onSelect(cell.date)}
                            >
                                {cell.day}
                                {cell.hasData && (
                                    <span className="slots-indicator">
                                        {cell.slots > 0 ? cell.slots : '0'}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
